/**
 * Hell, Dunkel or System — the switch in the sidebar footer.
 *
 * Expanded, three segments; collapsed to the icon rail, one button that steps
 * through the three, its icon showing the current choice.
 */

import { Monitor, Moon, Sun, type LucideIcon } from 'lucide-react'

import { useTheme, type ThemeMode } from '../../hooks/useTheme'
import { cn } from '../ui/styles'
import Tooltip from '../ui/Tooltip'

const MODES: ReadonlyArray<{ mode: ThemeMode; label: string; icon: LucideIcon }> = [
    { mode: 'light', label: 'Hell', icon: Sun },
    { mode: 'dark', label: 'Dunkel', icon: Moon },
    { mode: 'system', label: 'System', icon: Monitor },
]

const RING = 'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-400'

export default function ThemeSwitch({ collapsed }: { collapsed: boolean }) {
    const { mode, setMode } = useTheme()
    const index = MODES.findIndex((option) => option.mode === mode)
    const current = MODES[index]

    if (collapsed) {
        const next = MODES[(index + 1) % MODES.length]
        const Icon = current.icon
        return (
            <Tooltip label={`Darstellung: ${current.label} – wechseln zu ${next.label}`}>
                <button
                    type="button"
                    onClick={() => setMode(next.mode)}
                    aria-label={`Darstellung: ${current.label}. Wechseln zu ${next.label}`}
                    className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-full text-gray-300 transition-colors hover:bg-white/10 hover:text-white',
                        RING,
                    )}
                >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                </button>
            </Tooltip>
        )
    }

    return (
        <div role="group" aria-label="Darstellung" className="flex rounded-full bg-white/5 p-0.5">
            {MODES.map(({ mode: option, label, icon: Icon }) => {
                const active = option === mode
                return (
                    <button
                        key={option}
                        type="button"
                        onClick={() => setMode(option)}
                        aria-pressed={active}
                        className={cn(
                            'flex flex-1 items-center justify-center gap-1.5 rounded-full px-2 py-1 text-xs font-semibold transition-colors',
                            active ? 'bg-white/15 text-white' : 'text-gray-300 hover:text-white',
                            RING,
                        )}
                    >
                        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                        {label}
                    </button>
                )
            })}
        </div>
    )
}
